import { useState } from 'react';
import { useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { BackLink } from '@/components/back-link';
import { api } from '@/lib/api';
import { CampaignForm } from './campaign-form';
import { CampaignActions, type CampaignData } from './campaign-actions';
import { fmtDateTime } from '@/lib/format';
import { CampaignManagement } from './campaign-management';
import { CampaignStatus } from './campaign-status';

type Send = {
  id: string;
  toEmail: string;
  status: string;
  sentAt: string | null;
  openCount: number;
  clickCount: number;
  sandbox: boolean;
  error: string | null;
};

export async function campaignLoader({ params }: LoaderFunctionArgs) {
  const [campaign, templates, segments, sends] = await Promise.all([
    api<CampaignData>(`/campaigns/${params.id}`),
    api<{ id: string; name: string; kind: string }[]>('/templates'),
    api<{ id: string; name: string; kind: string; memberCount: number }[]>('/segments'),
    api<{ items: Send[]; total: number }>(`/campaigns/${params.id}/sends`),
  ]);
  return { campaign, templates, segments, sends };
}

export function CampaignPage() {
  const { campaign, templates, segments, sends } = useLoaderData<typeof campaignLoader>();
  const [schedule, setSchedule] = useState({ campaignId: campaign.id, when: '' });
  const editable = ['draft', 'test_sent'].includes(campaign.status);
  const segName = (sid: string) => segments.find((s) => s.id === sid)?.name ?? sid;

  return (
    <div className="space-y-5">
      <BackLink to="/campaigns">Campaigns</BackLink>
      <div className="space-y-4">
        <h1 className="break-words text-2xl">{campaign.name}</h1>
        <CampaignManagement key={campaign.id} campaign={campaign} />
        <dl className="grid gap-5 rounded-lg border bg-white p-5 text-sm xl:grid-cols-[auto_1fr_1fr]">
          <div className="space-y-2">
            <dt className="font-medium text-muted-foreground">State</dt>
            <dd>
              <CampaignStatus status={campaign.status} />
            </dd>
          </div>
          <div className="min-w-0 space-y-2">
            <dt className="font-medium text-muted-foreground">Template</dt>
            <dd className="break-words text-base">
              {templates.find((t) => t.id === campaign.templateId)?.name ?? campaign.templateId}{' '}
              <span className="whitespace-nowrap text-sm text-muted-foreground">
                · Version {campaign.templateVersion}
              </span>
            </dd>
          </div>
          <div className="min-w-0 space-y-2">
            <dt className="font-medium text-muted-foreground">Audience · union of segments</dt>
            <dd className="flex flex-wrap gap-2">
              {campaign.segmentIds.map((sid) => (
                <span
                  key={sid}
                  className="rounded-md bg-[var(--ff-primary-subtle)] px-2.5 py-1 text-[var(--ff-primary)]"
                >
                  {segName(sid)}
                </span>
              ))}
            </dd>
            {campaign.excludeSegmentIds.length > 0 && (
              <dd className="break-words text-muted-foreground">
                Excluding: {campaign.excludeSegmentIds.map(segName).join(', ')}
              </dd>
            )}
          </div>
        </dl>
      </div>

      <CampaignActions
        key={JSON.stringify([
          campaign.id,
          campaign.templateId,
          campaign.templateVersion,
          campaign.segmentIds,
          campaign.excludeSegmentIds,
        ])}
        campaign={campaign}
        segments={segments}
        when={schedule.campaignId === campaign.id ? schedule.when : ''}
        onWhenChange={(when) => setSchedule({ campaignId: campaign.id, when })}
      />

      {editable && (
        <CampaignForm
          key={`${campaign.id}-${campaign.name}-form`}
          templates={templates}
          segments={segments}
          initial={campaign}
        />
      )}

      {sends.total > 0 && (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <div className="border-b px-3 py-2 text-sm text-muted-foreground">
            {sends.total} sends (latest 50)
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Opens</th>
                <th>Clicks</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {sends.items.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.toEmail}
                    {s.sandbox && (
                      <span className="ml-1 rounded bg-amber-100 px-1 text-xs">sandbox</span>
                    )}
                  </td>
                  <td>{s.status}</td>
                  <td className="whitespace-nowrap text-xs">{fmtDateTime(s.sentAt)}</td>
                  <td>{s.openCount}</td>
                  <td>{s.clickCount}</td>
                  <td className="text-xs text-red-700">{s.error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
